import { useEffect, type ReactNode } from 'react';
import { useRouter } from 'next/router';
import { useAccount } from './AccountContext';
import { useCapabilities } from './CapabilitiesContext';
import { resolveCapabilityRoute } from './capabilityRouteConfig';
import { ErrorScreen } from '@/domains/shared/components/ErrorScreen';
import LoadingScreen from '@/domains/shared/components/LoadingScreen';
import { logToDatadog } from '@/domains/shared/analytics/datadogUtils';

export const CapabilityAccessGate = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const { userType } = useAccount();
  const { capabilities, isLoading, error } = useCapabilities();
  // One app-level gate applies whichever rule matches the current route.
  const targetPath = router.asPath.split(/[?#]/)[0];
  const resolution = resolveCapabilityRoute(targetPath, capabilities);
  const { allowed, gated, path: resolvedPath, ruleId } = resolution;
  const shouldGateRoute = userType === 'account' && gated;
  const shouldRedirect =
    shouldGateRoute && !allowed && resolvedPath !== targetPath;

  useEffect(() => {
    if (!shouldRedirect || isLoading || error) return;

    logToDatadog('info', 'Capability route gate redirect', {
      capabilityRouteGateRedirecting: true,
      ruleId,
      requestedPath: router.asPath,
      redirectedTo: resolvedPath,
    });
    void router.replace(resolvedPath);
  }, [error, isLoading, resolvedPath, router, ruleId, shouldRedirect]);

  if (!shouldGateRoute) return <>{children}</>;
  if (error) return <ErrorScreen error={error} />;
  if (isLoading || shouldRedirect) return <LoadingScreen />;

  return <>{children}</>;
};
